import { Component, OnInit, ViewChild } from '@angular/core';
import { Table } from 'primeng/table';
import { MessageService } from 'primeng/api';
import { User, IUserNew } from '@pages/authentication/interfaces/user';
import { SwalService } from '@shared/utils/swal.service';
import { UserService } from '@pages/user/services/user.service';
import { ExporterService } from '@shared/services/exporter/exporter.service';

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.css'],
  providers: [MessageService]
})
export class UserComponent implements OnInit {
  @ViewChild('dt') table!: Table;

  displayDialog: boolean = false;
  selectedUser: User | null = null;
  dataSource: IUserNew[] = [];
  loading: boolean = false;
  first: number = 0;
  rows: number = 10;
  totalRecords: number = 0;

  displayedColumns = [
    { field: 'id', header: 'Código', sortable: true },
    { field: 'description', header: 'Rol', sortable: true },
    { field: 'nombre', header: 'Nombre', sortable: true },
    { field: 'email', header: 'Correo', sortable: true },
    { field: 'phone', header: 'Télefono', sortable: true },
    { field: 'userName', header: 'Usuario', sortable: true },
    { field: 'active', header: 'Estado', sortable: true },
    { field: 'userCreated', header: 'Creado por', sortable: true },
    { field: 'dateCreated', header: 'Fecha de creación', sortable: true },
    { field: 'userModified', header: 'Editado por', sortable: true },
    { field: 'dateModified', header: 'Fecha de modificación', sortable: true },
    { field: 'actions', header: 'Acciones', sortable: false }
  ];

  private readonly _width: string = '380px';

  constructor(
    private _swalService: SwalService,
    private _userServices: UserService,
    private messageService: MessageService,
    private _exportService: ExporterService
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.loading = true;
    this._userServices.getUsersListNew().subscribe((res) => {
      // Si hay datos disponibles, los usamos independientemente del success
      if (res.data && res.data.length > 0) {
        this.dataSource = res.data;
        this.totalRecords = res.data.length;
      } else {
        this.dataSource = [];
        this.totalRecords = 0;
        if (!res.success) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Advertencia',
            detail: 'No se encontraron usuarios o hubo un problema con la consulta'
          });
        }
      }
      this.loading = false;
    }, (error) => {
      console.error('Error al cargar usuarios:', error);
      this.dataSource = [];
      this.totalRecords = 0;
      this.loading = false;
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al cargar la lista de usuarios'
      });
    });
  }

  openCreate() {
    this.selectedUser = null;
    this.displayDialog = true;
  }

  openUpdate(model: IUserNew) {
    // Convertir IUserNew a User para el formulario
    const userForForm: User = {
      id: model.id,
      rolId: model.rolId,
      email: model.email,
      phone: model.phone,
      userName: model.userName,
      active: model.active === 1, // Convertir number a boolean
      userCreated: model.userCreated,
      dateCreated: model.dateCreated,
      userModified: model.userModified || '',
      dateModified: model.dateModified || '',
      nombre: model.nombre,
      numeroDocumento: model.numeroDocumento,
      tipoUsuario: model.tipousuario,
      nivel: model.nivel.toString(),
      idioma: model.version,
      lineaAutorizada: model.lineA_AUTORIZADA,
      promotorInterno: model.codigoInterno,
      regional: model.grupoEmpresa,
      compania: model.sEmpresa,
      sucursal: model.sSucursal,
      administrador: model.administrador === 1,
      autorizaciones: model.overrideAutoriza === 1
    };
    this.selectedUser = userForForm;
    this.displayDialog = true;
  }

  onDialogClosed(): void {
    this.selectedUser = null;
    this.displayDialog = false;
  }

  onSave(event: { success: boolean; message: string }): void {
    if (event.success) {
      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: event.message
      });
      this.displayDialog = false;
      this.loadData();
    }
  }

  delete(model: IUserNew) {
    // Convertir IUserNew a User antes de eliminar
    const userForDelete: User = {
      id: model.id,
      rolId: model.rolId,
      email: model.email,
      phone: model.phone,
      userName: model.userName,
      active: model.active === 1, // Convertir number a boolean
      userCreated: model.userCreated,
      dateCreated: model.dateCreated,
      userModified: model.userModified || '',
      dateModified: model.dateModified || '',
      nombre: model.nombre
    };
    this.deleteUser(userForDelete);
  }

  private deleteUser(model: User) {
    // Por ahora usamos confirmación simple, puedes implementar un dialog de confirmación de PrimeNG si lo necesitas
    if (confirm('¿Está seguro de que desea eliminar este usuario?')) {
      this._userServices.delete(model).subscribe((res) => {
        if (res.exito === 1) {
          this.loadData();
          this.messageService.add({
            severity: 'success',
            summary: 'Éxito',
            detail: 'Usuario eliminado correctamente'
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al eliminar el usuario'
          });
        }
      });
    }
  }

  onPage(event: any): void {
    this.first = event.first;
    this.rows = event.rows;
    this.loadData();
  }

  getStatusText(active: any): string {
    // Manejar tanto boolean como number (0/1)
    const isActive = typeof active === 'boolean' ? active : active === 1;
    return isActive ? 'Activo' : 'Inactivo';
  }

  getStatusColor(active: any): string {
    // Manejar tanto boolean como number (0/1)
    const isActive = typeof active === 'boolean' ? active : active === 1;
    return isActive ? 'black' : 'red';
  }

  formatDate(date: any): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-ES');
  }

  exportarAExcel(): void {
    if (!this.dataSource || this.dataSource.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Advertencia',
        detail: 'No hay datos para exportar'
      });
      return;
    }

    this._exportService.exportToExcel(this.dataSource, 'Usuarios');
    
    this.messageService.add({
      severity: 'success',
      summary: 'Éxito',
      detail: `Se exportaron ${this.dataSource.length} registros a Excel`
    });
  }
}
